import React, { useState, useEffect, useRef, useCallback, FC, CSSProperties } from 'react'
import axios from 'axios'
import ReactMarkdown from 'react-markdown'
import { LoadingDots } from '../LoadingDots/LoadingDots'
import ConfirmationModal from '../ConfirmationModal/ConfirmationModal'
import styles from './Chatbot.module.css'

interface Message {
  id: string
  role: 'user' | 'assistant'
  content: string
  timestamp: Date | string
}

interface ChatbotProps {
  teamID: string
  customerID: string
  style?: CSSProperties
  className?: string
  webSocketImpl?: typeof WebSocket
}

interface APISession {
  sessionId: string
  updatedAt: string
  messages: Message[]
}

interface MarkdownRendererProps {
  text: string
}

const MarkdownRenderer: FC<MarkdownRendererProps> = ({ text }) => {
  if (!text) return null
  return (
    <div className={styles.markdownBody}>
      <ReactMarkdown>{text}</ReactMarkdown>
    </div>
  )
}

export const generateThreadId = (): string => {
  if (typeof window !== 'undefined' && window.crypto?.randomUUID) {
    return `chat-${window.crypto.randomUUID()}`
  }
  return `chat-${Math.random()
    .toString(36)
    .substring(2, 11)}`
}

const WELCOME_MESSAGE_CONTENT =
  "Hello! I'm your assistant. Ask me anything about this customer's fleet brand makeup, renewal statuses, average error rates, or recommended actions!"

export const Chatbot: FC<ChatbotProps> = ({
  teamID,
  customerID,
  style,
  className,
  webSocketImpl = typeof WebSocket !== 'undefined' ? WebSocket : (undefined as any),
}) => {
  const [messages, setMessages] = useState<Message[]>([
    {
      id: 'welcome',
      role: 'assistant',
      content: WELCOME_MESSAGE_CONTENT,
      timestamp: new Date(),
    },
  ])
  const [inputText, setInputText] = useState('')
  const [isTyping, setIsTyping] = useState(false)
  const [threadId, setThreadId] = useState(() => generateThreadId())
  const [showHistory, setShowHistory] = useState(false)
  const [sessions, setSessions] = useState<APISession[]>([])
  const [isLoadingSessions, setIsLoadingSessions] = useState(false)
  const [sessionIdToDelete, setSessionIdToDelete] = useState<string | null>(null)

  const messagesEndRef = useRef<HTMLDivElement>(null)
  const socketRef = useRef<WebSocket | null>(null)
  const hasInitializedRef = useRef(false)
  const isMountedRef = useRef(true)

  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
    }
  }, [])

  const fetchSessions = useCallback(
    async (signal?: AbortSignal) => {
      try {
        setIsLoadingSessions(true)
        const response = await axios.get<APISession[]>(
          `/api/teams/${teamID}/customers/${customerID}/briefs/health-check/chatbot/sessions`,
          { signal }
        )
        const sorted = (response?.data || []).sort((a, b) => {
          return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
        })
        if (isMountedRef.current) {
          setSessions(sorted)

          if (!hasInitializedRef.current) {
            hasInitializedRef.current = true
          }
        }
      } catch (err) {
        if (axios.isCancel(err)) {
          return
        }
        if (isMountedRef.current) {
          console.error('Failed to fetch chatbot sessions:', err)
        }
      } finally {
        if (isMountedRef.current) {
          setIsLoadingSessions(false)
        }
      }
    },
    [teamID, customerID]
  )

  useEffect(() => {
    const controller = new AbortController()
    fetchSessions(controller.signal)
    return () => {
      controller.abort()
    }
  }, [fetchSessions])

  // Keep track of the current streaming assistant message ID
  const activeBotMsgIdRef = useRef<string | null>(null)
  // Accumulate text chunks for the active message
  const activeBotTextRef = useRef<string>('')

  const scrollToBottom = () => {
    if (typeof messagesEndRef.current?.scrollIntoView === 'function') {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }

  useEffect(() => {
    scrollToBottom()
  }, [messages, isTyping])

  // Initialize and manage the single, persistent WebSocket connection
  useEffect(() => {
    if (!webSocketImpl) return
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
    const wsUrl = `${wsProtocol}//${window.location.host}/api/teams/${teamID}/customers/${customerID}/briefs/health-check/chatbot`
    const socket = new webSocketImpl(wsUrl)
    socketRef.current = socket

    socket.onopen = () => {
      if (!isMountedRef.current) return
      console.log('chatbot: WebSocket connection successfully opened')
    }

    socket.onmessage = (event: any) => {
      if (!isMountedRef.current) return
      try {
        const data = JSON.parse(event.data)
        if (data.text) {
          streamingTextReceived(data.text)
        }
        if (!data.partial) {
          setIsTyping(false)
          activeBotMsgIdRef.current = null
          fetchSessions()
        }
      } catch (err) {
        console.error('Failed to parse socket message:', err)
      }
    }

    socket.onerror = (err: any) => {
      if (!isMountedRef.current) return
      console.error('WebSocket error:', err)
      setIsTyping(false)
    }

    socket.onclose = () => {
      if (!isMountedRef.current) return
      console.log('chatbot: WebSocket connection closed')
      setIsTyping(false)
    }

    return () => {
      socket.onopen = null
      socket.onmessage = null
      socket.onerror = null
      socket.onclose = null
      socket.close()
    }
  }, [teamID, customerID, fetchSessions, webSocketImpl])

  const streamingTextReceived = (textChunk: string) => {
    activeBotTextRef.current += textChunk
    const targetId = activeBotMsgIdRef.current

    if (!targetId) return

    if (!isMountedRef.current) return

    setMessages(prev => {
      const exists = prev.some(m => m.id === targetId)
      if (exists) {
        return prev.map(m => (m.id === targetId ? { ...m, content: activeBotTextRef.current } : m))
      } else {
        return [
          ...prev,
          {
            id: targetId,
            role: 'assistant',
            content: activeBotTextRef.current,
            timestamp: new Date(),
          },
        ]
      }
    })
  }

  const handleSend = async (textToSend: string) => {
    const trimmed = textToSend.trim()
    if (!trimmed || isTyping || !socketRef.current) return

    const userMsgId = `user-${Date.now()}`
    const botMsgId = `bot-${Date.now()}`

    // Start a new streaming state
    activeBotMsgIdRef.current = botMsgId
    activeBotTextRef.current = ''

    // Add user message to state
    setMessages(prev => [
      ...prev,
      {
        id: userMsgId,
        role: 'user',
        content: trimmed,
        timestamp: new Date(),
      },
    ])
    setInputText('')
    setIsTyping(true)

    // Send the query down the persistent socket
    if (socketRef.current && webSocketImpl && socketRef.current.readyState === webSocketImpl.OPEN) {
      socketRef.current.send(
        JSON.stringify({
          text: trimmed,
          sessionId: threadId,
        })
      )
    } else {
      console.error('WebSocket is not open. Current state:', socketRef.current?.readyState)
      setIsTyping(false)
    }
  }

  const handleLoadChat = (sess: APISession) => {
    setThreadId(sess.sessionId)
    if (sess.messages && sess.messages.length > 0) {
      setMessages(sess.messages)
    } else {
      setMessages([
        {
          id: 'welcome',
          role: 'assistant',
          content: WELCOME_MESSAGE_CONTENT,
          timestamp: new Date(),
        },
      ])
    }
    setShowHistory(false)
  }

  const handleNewChat = () => {
    const newId = generateThreadId()
    setThreadId(newId)
    setMessages([
      {
        id: 'welcome',
        role: 'assistant',
        content: WELCOME_MESSAGE_CONTENT,
        timestamp: new Date(),
      },
    ])
    setShowHistory(false)
  }

  const handleDeleteChat = async (id: string) => {
    try {
      await axios.delete(`/api/teams/${teamID}/customers/${customerID}/briefs/health-check/chatbot/sessions/${id}`)
      await fetchSessions()
      if (isMountedRef.current && id === threadId) {
        handleNewChat()
      }
    } catch (err) {
      if (isMountedRef.current) {
        console.error('Failed to delete session:', err)
      }
    }
  }

  const suggestionChips = ['Fleet Health Summary', 'Any renewal risks?', 'What is the error rate?']

  return (
    <div
      className={`${styles.chatbotContainer}${className ? ` ${className}` : ''}`}
      // eslint-disable-next-line react/forbid-dom-props -- pass-through component style prop
      style={style}
    >
      {/* Header */}
      <div className={styles.chatbotHeader}>
        <div className={styles.chatbotTitleArea}>
          <span className={styles.sparkle}>✨</span>
          <div>
            <h3 className={styles.chatbotTitle}>Health Check Chatbot</h3>
            <span className={styles.onlineBadge}>
              <span className={styles.onlineDot}></span>
              Online Context Assistant
            </span>
          </div>
        </div>

        <div className={styles.headerActions}>
          <button onClick={handleNewChat} className={styles.actionButton}>
            ➕ New Chat
          </button>
          <button
            onClick={() => setShowHistory(prev => !prev)}
            className={`${styles.actionButton}${showHistory ? ` ${styles.actionButtonActive}` : ''}`}
          >
            🕒 {showHistory ? 'Chat' : 'History'}
          </button>
        </div>
      </div>

      {showHistory ? (
        /* Chat History Panel */
        <div className={styles.historyPanel}>
          <div className={styles.historyHeader}>PREVIOUS CHATS</div>

          {isLoadingSessions ? (
            <div className={styles.loadingSessionsWrapper}>
              <div className={styles.loadingSessionsSpinner}>
                <LoadingDots />
              </div>
              <span className={styles.loadingSessionsLabel}>Loading your chat history...</span>
            </div>
          ) : sessions.length === 0 ? (
            <div className={styles.noSessionsWrapper}>
              <span>No previous chats found.</span>
              <span className={styles.noSessionsLabel}>Any messages you send will be saved to your history.</span>
            </div>
          ) : (
            <div className={styles.sessionsList}>
              {sessions.map(sess => {
                const firstUserMsg = sess.messages?.find(m => m.role === 'user')?.content || 'Empty Chat'
                const formattedDate = new Date(sess.updatedAt).toLocaleString(undefined, {
                  month: 'short',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })
                const isActive = sess.sessionId === threadId

                return (
                  <div
                    key={sess.sessionId}
                    className={`${styles.sessionItem}${isActive ? ` ${styles.sessionItemActive}` : ''}`}
                    onClick={() => handleLoadChat(sess)}
                  >
                    <div className={styles.sessionItemDetails}>
                      <div className={styles.sessionItemText}>{firstUserMsg}</div>
                      <span className={styles.sessionItemDate}>{formattedDate}</span>
                    </div>

                    <button
                      onClick={e => {
                        e.stopPropagation()
                        setSessionIdToDelete(sess.sessionId)
                      }}
                      className={styles.deleteSessionBtn}
                      title='Delete chat'
                    >
                      🗑️
                    </button>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      ) : (
        /* Main Scrollable Content Area */
        <div className={styles.messagesContainer}>
          {/* Messages */}
          {messages.map(msg => {
            const isUser = msg.role === 'user'
            return (
              <div
                key={msg.id}
                className={`${styles.messageWrapper} ${
                  isUser ? styles.messageWrapperUser : styles.messageWrapperAssistant
                }`}
              >
                <div
                  className={`${styles.messageBubble} ${
                    isUser ? styles.messageBubbleUser : styles.messageBubbleAssistant
                  }`}
                >
                  {isUser ? msg.content : <MarkdownRenderer text={msg.content} />}
                </div>
              </div>
            )
          })}

          {/* Typing indicator */}
          {isTyping && (
            <div className={styles.typingIndicatorWrapper}>
              <div className={styles.typingIndicator}>
                <span className={styles.typingDot}></span>
                <span className={styles.typingDot}></span>
                <span className={styles.typingDot}></span>
              </div>
            </div>
          )}

          {/* Suggestion Chips */}
          {messages.length === 1 && (
            <div className={styles.suggestionsWrapper}>
              {suggestionChips.map(chip => (
                <button key={chip} onClick={() => handleSend(chip)} className={styles.suggestionChip}>
                  {chip}
                </button>
              ))}
            </div>
          )}

          {/* Input Form */}
          <form
            onSubmit={e => {
              e.preventDefault()
              handleSend(inputText)
            }}
            className={styles.inputForm}
          >
            <input
              type='text'
              value={inputText}
              onChange={e => setInputText(e.target.value)}
              placeholder="Ask about this customer's health..."
              disabled={isTyping}
              className={styles.inputField}
            />
            <button
              type='submit'
              disabled={!inputText.trim() || isTyping}
              className={`${styles.sendBtn}${!inputText.trim() || isTyping ? ` ${styles.sendBtnDisabled}` : ''}`}
            >
              Send
            </button>
          </form>

          <div ref={messagesEndRef} />
        </div>
      )}

      {sessionIdToDelete !== null && (
        <ConfirmationModal
          title='Delete Chat Thread'
          message='Are you sure you want to delete this chat thread?'
          onClose={() => setSessionIdToDelete(null)}
          onYes={() => {
            handleDeleteChat(sessionIdToDelete)
          }}
        />
      )}
    </div>
  )
}
